import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import Message, { MessageProps } from '../message/Message';
import './toast.scss';

export interface ToastProps {
  id?: string;
  message: MessageProps;
  duration?: number;
  open?: boolean;
  onClose?: () => void;
  className?: string;
}

type ToastListener = (toast: ToastProps) => void;

class ToastService {
  private listeners = new Set<ToastListener>();

  subscribe(listener: ToastListener) {
    this.listeners.add(listener);
  }

  unsubscribe(listener: ToastListener) {
    this.listeners.delete(listener);
  }

  add(toast: ToastProps) {
    this.listeners.forEach((listener) => listener(toast));
  }

  remove(id: string) {
    this.listeners.forEach((listener) => {
      listener({ id, open: false, message: { type: 'info', description: '' } });
    });
  }
}

export const toastService = new ToastService();

const getStackRoot = (() => {
  let root: HTMLDivElement | null = null;
  return () => {
    if (root) return root;
    root = document.createElement('div');
    root.className = 'toast-stack';
    document.body.appendChild(root);
    return root;
  };
})();

const Toast: React.FC<ToastProps> = ({
  message,
  duration = 3000,
  open = true,
  onClose,
  className = '',
}) => {
  const [visible, setVisible] = useState(open);

  useEffect(() => setVisible(open), [open]);

  useEffect(() => {
    if (!visible || !duration) return;
    const timer = window.setTimeout(() => setVisible(false), duration);
    return () => clearTimeout(timer);
  }, [visible, duration]);

  const handleAnimationEnd = () => {
    if (!visible) onClose?.();
  };

  const handleActionClick = () => {
    if (message.actions?.some((action) => action.shouldClose)) {
      setVisible(false);
    }
    message.onActionClick?.();
  };

  if (!open && !visible) return null;

  return ReactDOM.createPortal(
    <article
      onAnimationEnd={handleAnimationEnd}
      className={classNames(
        'toast',
        visible ? 'toast--enter' : 'toast--exit',
        className
      )}
    >
      <Message {...message} onActionClick={handleActionClick} />
    </article>,
    getStackRoot()
  );
};

const ToastManager: React.FC = () => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const addToast = useCallback((toast: ToastProps) => {
    const id = toast.id ?? uuidv4();
    setToasts((prev) => {
      const existing = prev.find((t) => t.id === id);
      if (existing) {
        return prev.map((t) => (t.id === id ? { ...t, ...toast, id } : t));
      }
      return [...prev, { ...toast, id, open: toast.open ?? true }];
    });
  }, []);

  useEffect(() => {
    toastService.subscribe(addToast);
    return () => toastService.unsubscribe(addToast);
  }, [addToast]);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <>
      {toasts.map(({ id = '', ...toastProps }) => (
        <Toast
          key={id}
          {...toastProps}
          onClose={() => removeToast(id)}
          open={toastProps.open}
        />
      ))}
    </>
  );
};

export default ToastManager;
